// Same-origin client for api-worker (/api/v1). Mirrors contract C1:
// errors are `{ error: { code, message, details? } }`; mutations need X-CSRF-Token.
// The CSRF token is kept in memory only — never in localStorage/sessionStorage (UI-SEC-2).

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? {};
  }
}

const BASE = '/api/v1';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const NETWORK_ERROR: ApiErrorBody = {
  code: 'NETWORK',
  message: 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองใหม่',
};

let csrfToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

/** Registered by the app shell: clears session state and sends the user to /login. */
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

export function resetApiState() {
  csrfToken = null;
  onUnauthorized = null;
}

async function send(
  method: string,
  path: string,
  body: unknown,
  extra: Record<string, string> = {},
): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  // FormData (media upload) sets its own multipart boundary header.
  const multipart = body instanceof FormData;
  if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
  if (MUTATING.has(method) && csrfToken) headers['X-CSRF-Token'] = csrfToken;
  try {
    return await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(body === undefined ? {} : { body: multipart ? body : JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError(0, NETWORK_ERROR);
  }
}

async function parse<T>(res: Response): Promise<T> {
  const json: unknown = await res.json().catch(() => null);
  if (res.ok) {
    const token = (json as { csrfToken?: unknown } | null)?.csrfToken;
    if (typeof token === 'string') csrfToken = token;
    return json as T;
  }
  const err = (json as { error?: ApiErrorBody } | null)?.error;
  throw new ApiError(
    res.status,
    err ?? { code: 'INTERNAL', message: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' },
  );
}

async function refreshCsrf(): Promise<boolean> {
  const res = await send('GET', '/auth/session', undefined);
  if (!res.ok) return false;
  await parse(res);
  return true;
}

export interface RequestOptions {
  /** Pages that handle 401 themselves (login, 2FA) opt out of the global redirect. */
  skipUnauthorizedRedirect?: boolean;
  /** Extra request headers, e.g. Idempotency-Key on publish (u7). */
  headers?: Record<string, string>;
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  let res = await send(method, path, body, opts.headers);

  // A rotated or missing token: refresh it from the session once, then retry.
  if (res.status === 403 && MUTATING.has(method)) {
    const peek = (await res
      .clone()
      .json()
      .catch(() => null)) as { error?: ApiErrorBody } | null;
    if (peek?.error?.details?.['reason'] === 'csrf' && (await refreshCsrf())) {
      res = await send(method, path, body, opts.headers);
    }
  }

  if (res.status === 401 && !opts.skipUnauthorizedRedirect) {
    csrfToken = null;
    onUnauthorized?.();
  }
  return parse<T>(res);
}

/** POST that returns text (CSV export chunks) plus a response header, e.g. X-Next-Cursor. */
export async function postText(
  path: string,
  header: string,
): Promise<{ text: string; header: string | null }> {
  const res = await send('POST', path, undefined);
  if (!res.ok) return parse(res);
  return { text: await res.text(), header: res.headers.get(header) };
}

/** POST that returns a file (legal-log downloads). */
export async function postBlob(path: string, body: unknown): Promise<Blob> {
  const res = await send('POST', path, body);
  if (!res.ok) return parse(res);
  return res.blob();
}

/** GET that returns text (CSV report). */
export async function getText(path: string): Promise<string> {
  const res = await send('GET', path, undefined);
  if (!res.ok) return parse(res);
  return res.text();
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body: unknown = {}, opts?: RequestOptions) =>
    request<T>('POST', path, body, opts),
  patch: <T>(path: string, body: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, body, opts),
  upload: <T>(path: string, form: FormData, opts?: RequestOptions) =>
    request<T>('POST', path, form, opts),
  delete: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('DELETE', path, body, opts),
};
